import { useState } from 'react';
import { Wallet, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { cn, errorMessage } from '@/lib/utils';

interface WalletButtonsProps {
  isLightCard: boolean;
  textColor: string;
  cardBg: string;
  cardId?: string;
  /** Set false in the editor preview so clicks don't navigate. */
  interactive?: boolean;
}

/**
 * Single "Add to Google Wallet" button. Apple Wallet support is deferred
 * until we're willing to commit to the $99/yr Apple Developer Program +
 * pass-type-ID setup — keeping the button visible while it's disabled
 * just confused users into thinking we shipped something we didn't.
 */
export function WalletButtons({
  isLightCard,
  textColor,
  cardBg,
  cardId,
  interactive = true,
}: WalletButtonsProps) {
  const [busy, setBusy] = useState(false);

  async function onGoogleClick() {
    if (!interactive) return;
    if (!cardId) {
      toast({ title: 'Wallet unavailable', description: 'No card id in scope', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      if (!token) throw new Error('Sign in first');
      const res = await fetch('/api/wallet/google', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ card_id: cardId }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.saveUrl) {
        if (res.status === 503) {
          toast({
            title: 'Google Wallet coming soon',
            description: 'Pass generation not configured yet.',
          });
        } else {
          toast({
            title: 'Wallet failed',
            description: json?.error ?? `HTTP ${res.status}`,
            variant: 'destructive',
          });
        }
        return;
      }
      // Redirect the user to Google's "Add to Wallet" landing page.
      window.location.href = json.saveUrl;
    } catch (e) {
      toast({
        title: 'Wallet failed',
        description: errorMessage(e),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  }

  const borderColor = isLightCard ? 'rgba(10,10,10,0.12)' : 'rgba(255,255,255,0.12)';

  return (
    <button
      onClick={onGoogleClick}
      className={cn(
        'flex h-10 w-full items-center justify-center gap-1.5 rounded-full text-[11px] font-medium tracking-tight transition-transform active:scale-[0.98]',
      )}
      style={{ border: `1px solid ${borderColor}`, color: textColor, backgroundColor: cardBg }}
      aria-label="Add to Google Wallet"
      disabled={!interactive || busy}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wallet className="h-3.5 w-3.5" />}
      Add to Google Wallet
    </button>
  );
}
